import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Loader2, X, UserCheck, Wrench } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { useFilial } from '@/lib/filial'

// Cadastro leve de VENDEDOR ou TÉCNICO/MECÂNICO (sem login).
// tipo='vendedor' → usado no PDV/Pedido. tipo='tecnico' → usado na OS (Serviços).
export default function ColaboradoresPage({ tipo }: { tipo: 'vendedor' | 'tecnico' }) {
  const qc = useQueryClient()
  const [editar, setEditar] = useState<any | null>(null)
  const [novo, setNovo] = useState(false)
  const ehVend = tipo === 'vendedor'
  const titulo = ehVend ? 'Vendedores' : 'Técnicos / Mecânicos'
  const Icone = ehVend ? UserCheck : Wrench
  const filiais = useFilial(s => s.filiais)
  const filialAtual = useFilial(s => s.filialAtual)
  const modoConsolidado = useFilial(s => s.modoConsolidado)
  const nomeFilial = (fid: number | null | undefined) =>
    fid ? (filiais.find(f => f.id === fid)?.nome_fantasia || filiais.find(f => f.id === fid)?.razao_social || `Filial ${fid}`) : '—'

  // Lista restrita à filial atual (+ legados sem filial). Em modo consolidado/sem filial = todas.
  const { data: lista = [], isLoading } = useQuery<any[]>({
    queryKey: ['colaboradores', tipo, modoConsolidado ? null : filialAtual?.id ?? null],
    queryFn: async () => (await api.get('/cadastros/colaboradores', { params: { tipo, filial: true } })).data,
  })

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Icone className="text-brand-600" size={22} />
          <h1 className="text-xl font-bold text-ink-900">{titulo}</h1>
        </div>
        <button onClick={() => setNovo(true)} className="btn-primary flex items-center gap-1.5 !py-2">
          <Plus size={16} /> Novo {ehVend ? 'vendedor' : 'técnico'}
        </button>
      </div>
      <p className="text-sm text-ink-500 mb-5">
        {ehVend
          ? 'Vendedores pra selecionar no PDV e no Pedido. Não precisam de login.'
          : 'Técnicos/mecânicos pra selecionar na Ordem de Serviço. Não precisam de login.'}
      </p>

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-ink-400" /></div>
      ) : lista.length === 0 ? (
        <div className="text-center text-ink-400 py-10 bg-white rounded-xl border border-ink-200">
          Nenhum {ehVend ? 'vendedor' : 'técnico'} cadastrado.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-ink-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 text-ink-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">Nome</th>
                <th className="text-left px-4 py-2">Filial</th>
                <th className="text-left px-4 py-2">Código</th>
                <th className="text-left px-4 py-2">Telefone</th>
                {ehVend && <th className="text-right px-4 py-2">Comissão</th>}
                <th className="text-center px-4 py-2">Ativo</th>
                <th className="px-4 py-2 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {lista.map(c => (
                <tr key={c.id} className={`border-t border-ink-100 hover:bg-ink-50 ${!c.ativo ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-2 font-medium">{c.nome}</td>
                  <td className="px-4 py-2 text-ink-600">{nomeFilial(c.filial_id)}</td>
                  <td className="px-4 py-2 font-mono text-ink-500">{c.codigo || '—'}</td>
                  <td className="px-4 py-2 text-ink-500">{c.telefone || '—'}</td>
                  {ehVend && <td className="px-4 py-2 text-right font-mono">{Number(c.comissao_pct || 0).toFixed(2)}%</td>}
                  <td className="px-4 py-2 text-center">{c.ativo ? '✅' : '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => setEditar(c)} className="text-ink-400 hover:text-brand-600"><Pencil size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(novo || editar) && (
        <ModalColaborador
          tipo={tipo} colaborador={editar}
          onClose={() => { setNovo(false); setEditar(null) }}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['colaboradores', tipo] }); setNovo(false); setEditar(null) }}
        />
      )}
    </div>
  )
}

function ModalColaborador({ tipo, colaborador, onClose, onSaved }: {
  tipo: 'vendedor' | 'tecnico'; colaborador: any | null; onClose: () => void; onSaved: () => void
}) {
  const ehVend = tipo === 'vendedor'
  const filiais = useFilial(s => s.filiais)
  const filialAtual = useFilial(s => s.filialAtual)
  const [nome, setNome] = useState(colaborador?.nome || '')
  const [codigo, setCodigo] = useState(colaborador?.codigo || '')
  const [telefone, setTelefone] = useState(colaborador?.telefone || '')
  const [comissao, setComissao] = useState(String(colaborador?.comissao_pct || 0))
  const [filialId, setFilialId] = useState<number | ''>(colaborador?.filial_id ?? filialAtual?.id ?? '')
  const [ativo, setAtivo] = useState(colaborador?.ativo ?? true)
  const isEdicao = !!colaborador

  const salvar = useMutation({
    mutationFn: () => {
      const body = { tipo, nome, codigo: codigo || undefined, telefone: telefone || undefined,
        comissao_pct: Number(comissao) || 0, filial_id: filialId === '' ? null : Number(filialId), ativo }
      return isEdicao
        ? api.put(`/cadastros/colaboradores/${colaborador.id}`, body)
        : api.post('/cadastros/colaboradores', body)
    },
    onSuccess: () => { toast.success(isEdicao ? 'Atualizado' : 'Cadastrado'); onSaved() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-ink-900">
            {isEdicao ? 'Editar' : 'Novo'} {ehVend ? 'vendedor' : 'técnico'}
          </h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-600"><X size={20} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-ink-500 uppercase">Nome *</label>
            <input value={nome} onChange={e => setNome(e.target.value.toUpperCase())} autoFocus
              className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-ink-500 uppercase">Código</label>
              <input value={codigo} onChange={e => setCodigo(e.target.value)}
                className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-ink-500 uppercase">Telefone</label>
              <input value={telefone} onChange={e => setTelefone(e.target.value)}
                className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-500 uppercase">Filial *</label>
            <select value={filialId} onChange={e => setFilialId(e.target.value === '' ? '' : Number(e.target.value))}
              className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm bg-white">
              <option value="">— Sem filial (todas) —</option>
              {filiais.map(f => (
                <option key={f.id} value={f.id}>{f.nome_fantasia || f.razao_social}</option>
              ))}
            </select>
            <p className="text-[11px] text-ink-400 mt-1">
              {ehVend ? 'No PDV/Pedido só aparecem os vendedores da filial selecionada.' : 'A filial deste cadastro.'}
            </p>
          </div>
          {ehVend && (
            <div>
              <label className="text-xs font-semibold text-ink-500 uppercase">Comissão (%)</label>
              <input type="number" step="0.01" min="0" value={comissao} onChange={e => setComissao(e.target.value)}
                className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm font-medium text-ink-700">
            <input type="checkbox" checked={ativo} onChange={e => setAtivo(e.target.checked)} /> Ativo
          </label>
          <button onClick={() => { if (!nome.trim()) { toast.error('Informe o nome'); return } salvar.mutate() }}
            disabled={salvar.isPending}
            className="w-full btn-primary disabled:opacity-50">
            {salvar.isPending ? <Loader2 size={16} className="animate-spin" /> : (isEdicao ? 'Salvar' : 'Cadastrar')}
          </button>
        </div>
      </div>
    </div>
  )
}
